import { word } from "@modules/core/utils/i18n"
import { ProgramBundleTypeInput } from "@modules/formik/components/programBundleTypeInput"
import { ProgramBundleVisibilityInput } from "@modules/formik/components/programBundleVisibilityInput"
import { ProgramBundleFormPageProps } from "@modules/programBundle/components/programBundleForm"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { ArrowRight } from "lucide-react"
import { useState } from "react"
import { useTheme } from "styled-components"

export const ProgramBundleFormFirstPage = ({
  formik,
  isFormFieldInValid,
  getFormErrorMessage,
  handleSubmit,
  isEdited,
}: ProgramBundleFormPageProps) => {
  const theme = useTheme()
  const [loadingImageUpload, setLoadingImageUpload] = useState(false)
  const { hideSidebar } = useSidebar()

  return (
    <div className="flex flex-column gap-2">
      {/* TITLE */}
      <div className="flex flex-column">
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("programBundle.form.firstPage.step")}
        </HUText>
        <HUText fontStyle="HM" color={theme.neutralColor900} className="mt-4">
          {word("programBundle.form.firstPage.title")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor900} className="mt-1">
          {word("programBundle.form.firstPage.subtitle")}
        </HUText>
      </div>

      {/* COVER IMAGE */}
      <div className="flex flex-column mt-4">
        <FormikInput
          type="image"
          imageProps={{
            type: "cover",
            placeHolder: {
              message: word("program.form.uploadImage.text1"),
            },
            showPreviewAsCover: true,
            prefixPath: "/programs",
            onStartUpload: () => setLoadingImageUpload(true),
            onEndUpload: () => setLoadingImageUpload(false),
          }}
          label={word("program.form.image.label")}
          name="imageWeb"
          getFieldProps={formik.getFieldProps}
          setFieldValue={formik.setFieldValue}
          isInvalid={isFormFieldInValid("imageWeb")}
          error={getFormErrorMessage("imageWeb")}
        />
      </div>

      {/* TITLE */}
      <FormikInput
        flex
        label={word("offer.form.title.label")}
        isRequiredInput
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("name")}
        name="name"
        placeHolder={word("offer.form.title.placeholder")}
        error={getFormErrorMessage("name")}
      />

      {/* DESCRIPTION */}
      <FormikInput
        isRequiredInput
        label={word("offer.form.description.label")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("description")}
        name="description"
        error={getFormErrorMessage("description")}
        type="textarea"
        formikStyle={{ flex: 1 }}
        placeHolder={word("offer.form.description.placeholder")}
      />

      {/* TYPE */}
      <ProgramBundleTypeInput
        label={word("programBundle.form.typeOfPrograms.label")}
        name="type"
        isRequiredInput={true}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        isInvalid={isFormFieldInValid("type")}
        error={getFormErrorMessage("type")}
        isSwitchEnabled={formik.values.programIds.length === 0 && !isEdited}
        className="flex flex-column w-full mb-3"
      />

      {/* VISIBILITY */}
      <ProgramBundleVisibilityInput
        label={"Visibility"}
        name="visibility"
        isRequiredInput={true}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        isInvalid={isFormFieldInValid("visibility")}
        error={getFormErrorMessage("visibility")}
        className="flex flex-column w-full mb-3"
        switchProps={{
          checked: true,
        }}
      />

      {/* NEXT BUTTON */}
      <div className="flex gap-5 mt-5 mb-6">
        <HUButton
          text={word("global.cancel")}
          onClick={() => hideSidebar()}
          type="Rounded"
          size="M"
          colorType="Quaternary"
          fullWidth
        />
        <HUButton
          text={word("global.next")}
          onClick={handleSubmit}
          type="Right_Icon"
          size="M"
          colorType="Primary"
          fullWidth
          icon={{
            iconView: <ArrowRight size={18} color={theme.mainBlack} />,
          }}
          disabled={loadingImageUpload}
        />
      </div>
    </div>
  )
}
